// Admin-approved school join — shared helpers.
// A join request uses a DETERMINISTIC id ("<uid>_<schoolId>") so the firestore
// rules join-gate (hasApprovedJoin) can look one up without a query.
import { firestore } from "./FirebaseConfig";
import {
  doc, collection, query, where, getDoc, getDocs, writeBatch,
  setDoc, updateDoc, deleteDoc, arrayUnion, serverTimestamp,
} from "firebase/firestore";

export const joinRequestId = (uid, schoolId) => `${uid}_${schoolId}`;

// Create/refresh a PENDING request to join an existing school. The user stays
// in their current (provisional) school until an admin approves.
export async function requestToJoinSchool({ uid, user, school }) {
  const id = joinRequestId(uid, school.id);
  await setDoc(doc(firestore, "joinRequests", id), {
    requesterUid: uid,
    requesterEmail: user?.email || "",
    requesterName: user?.displayName || "",
    schoolId: school.id,
    schoolName: school.schoolName || "",
    status: "pending",
    createdAt: serverTimestamp(),
  });
  return id;
}

// Once approved, move the requester into the school and consume the request.
// Order matters:
//   1. The user-doc schoolId write must happen while the approved request still
//      exists (the rules join-gate reads it to authorize the schoolId change).
//   2. THEN migrate the requester's existing classes + private students onto the
//      new schoolId. Without this they stay stranded on the old (provisional)
//      schoolId and become invisible to every schoolId-scoped roster query — the
//      teacher "can't see their students". Mirrors SchoolAdmin's move logic
//      (owner-authorized writes: teacherId==uid for classes, addedBy==uid for
//      students), scoped to docs actually on the old school so we never touch
//      another tenant's data.
//   3. THEN delete the request.
export async function finalizeApprovedJoin({ uid, schoolId, schoolName }) {
  const userRef = doc(firestore, "users", uid);
  const userSnap = await getDoc(userRef);
  const oldSchoolId = userSnap.exists() ? userSnap.data().schoolId : null;

  await updateDoc(userRef, { schoolId, schoolName });
  await updateDoc(doc(firestore, "schools", schoolId), { teachers: arrayUnion(uid) });

  if (oldSchoolId && oldSchoolId !== schoolId) {
    const [classSnap, studentSnap] = await Promise.all([
      getDocs(query(collection(firestore, "classes"),  where("teacherId", "==", uid))),
      getDocs(query(collection(firestore, "students"), where("addedBy", "==", uid))),
    ]);
    const batch = writeBatch(firestore);
    classSnap.docs
      .filter((d) => d.data().schoolId === oldSchoolId)
      .forEach((d) => batch.update(d.ref, { schoolId }));
    studentSnap.docs
      .filter((d) => d.data().schoolId === oldSchoolId && d.data().shared !== true)
      .forEach((d) => batch.update(d.ref, { schoolId }));
    await batch.commit();
  }

  await deleteDoc(doc(firestore, "joinRequests", joinRequestId(uid, schoolId)));
}
